import {
  getPageContentsWithProducts,
  IPrismicCustomTypeContent,
  SlicesContainer,
} from '@screens/SlicesContainer';
import CustomError from '@screens/CustomError';
import { prismicService } from '@services/businessContainer';
import { formatPageContents } from '@utils/prismic';
import { NotFoundError, PrismicDocument } from '@prismicio/client';
import { getChannelFromLocale } from '@monofront/business/utils';
import {
  HOME_SLICES_LINKS,
  PRODUCT_BUNDLES_LINKS,
  WIDGETS_LINKS,
} from '@constants/prismic';
import { NextSeo } from 'next-seo';
import { useTranslations } from 'next-intl';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { injectLayoutData, LFPage } from '@utils/layout';
import { GetStaticProps } from 'next';
import { useFeatureIsOn } from '@monofront/business/contexts';
import logger from '@utils/logger';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

type LandingPageProps = {
  pageContents?: IPrismicCustomTypeContent[];
  abTestContents?: IPrismicCustomTypeContent[];
  featureFlagKey: string;
  variant?: boolean | null;
  SEOConfiguration: {
    title?: string;
    description?: string;
    noindex?: boolean;
  };
};
const LandingPage: LFPage<LandingPageProps> = ({
  pageContents,
  abTestContents,
  featureFlagKey,
  SEOConfiguration,
}) => {
  const tSEODEFAULT = useTranslations('SEO.DEFAULT');
  const router = useRouter();

  const isAbTestEnabled = useFeatureIsOn(featureFlagKey);

  if (!pageContents || pageContents.length === 0) {
    return <CustomError statusCode={404} />;
  }

  const pathUrl = router.asPath.split('?')[0];

  return (
    <>
      <NextSeo
        title={SEOConfiguration?.title || tSEODEFAULT('title')}
        description={
          SEOConfiguration?.description || tSEODEFAULT('description')
        }
        noindex={SEOConfiguration?.noindex}
        canonical={`${appBaseUrl}${pathUrl}`}
        openGraph={{
          url: `${appBaseUrl}${pathUrl}`,
          title: SEOConfiguration?.title || tSEODEFAULT('title'),
          description:
            SEOConfiguration?.description || tSEODEFAULT('description'),
        }}
      />
      <SlicesContainer
        pageContents={
          isAbTestEnabled && abTestContents ? abTestContents : pageContents
        }
      />
      <style jsx>
        {`
          .loader-container {
            min-height: calc(100vh - 100px);
            display: flex;
            align-items: center;
            padding-bottom: 100px;
          }
        `}
      </style>
    </>
  );
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = injectLayoutData<
  LandingPageProps,
  GetStaticProps<LandingPageProps>
>(async ({ params, previewData, locale }) => {
  if (!params?.slug) {
    return {
      notFound: true,
    };
  }

  try {
    const document: PrismicDocument = await prismicService.getDocumentByUid(
      'landingpage',
      params.slug.toString(),
      previewData,
      locale
        ? {
            fetchLinks: [
              ...HOME_SLICES_LINKS,
              ...WIDGETS_LINKS,
              ...PRODUCT_BUNDLES_LINKS,
            ],
            ...(locale ? { lang: `${locale}-${locale}` } : {}),
          }
        : {},
    );

    if (!document || !document.data.is_automatically_accessible) {
      return {
        notFound: true,
      };
    }

    const pageContentsWithProducts = await getPageContentsWithProducts(
      formatPageContents(document.data.group),
      getChannelFromLocale(locale),
    );

    const abTestContentsWithProducts: IPrismicCustomTypeContent[] =
      await getPageContentsWithProducts(
        formatPageContents(document.data.ab_test_group),
        getChannelFromLocale(locale),
      );

    return {
      props: {
        footerType: document.data.footer_selector,
        header: {
          type: document.data.header_selector,
          headerLightCtaLink: document.data.header_light_cta_link,
          headerLightCtaText: document.data.header_light_cta_text,
        },
        pageContents: pageContentsWithProducts,
        abTestContents: abTestContentsWithProducts,
        featureFlagKey: document.data.feature_flag_key ?? '',
        SEOConfiguration: {
          title: document.data.seo_title ?? null,
          description: document.data.seo_description ?? null,
          noindex: document.data.is_indexed === false,
        },
      },
      revalidate: 1800,
    };
  } catch (error) {
    if (error instanceof NotFoundError) {
      logger.warn('Landing page not found', { slug: params.slug });

      return {
        notFound: true,
      };
    }

    throw error;
  }
});

export default LandingPage;
